import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useLeague } from '@/contexts/LeagueContext';
import { cn } from '@/lib/utils';
import {
  Trophy,
  Crown,
  Skull,
  Bomb,
  Target,
  TrendingUp,
  Zap,
  Flame,
  Star,
  AlertTriangle,
  Award,
  ArrowUpDown,
} from 'lucide-react';

interface AwardData {
  id: string;
  icon: React.ReactNode;
  label: string;
  teamName: string;
  value: string;
  color: string; // tailwind text color class using tokens
  description: string;
}

interface SeasonAwardsProps {
  standings: { team_id: string; team_name: string; wins: number; losses: number; ties: number; total_points: number; streak: string }[];
}

const SeasonAwards = ({ standings }: SeasonAwardsProps) => {
  const { activeLeague, teams } = useLeague();
  const [awards, setAwards] = useState<AwardData[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCompleted, setIsCompleted] = useState(false);

  useEffect(() => {
    if (!activeLeague || standings.length === 0) {
      setLoading(false);
      return;
    }

    setIsCompleted(activeLeague.status === 'completed');
    computeAwards();
  }, [activeLeague, standings, teams]);

  const computeAwards = async () => {
    if (!activeLeague) return;

    // Fetch sabotage data + matchup data in parallel
    const [sabRes, matchRes, tradeRes] = await Promise.all([
      supabase
        .from('applied_sabotages')
        .select('applied_by_team_id, target_team_id, cost, sabotage_name')
        .eq('league_id', activeLeague.id)
        .eq('status', 'active'),
      supabase
        .from('matchups')
        .select('home_team_id, away_team_id, home_score, away_score, status, week')
        .eq('league_id', activeLeague.id)
        .eq('status', 'final'),
      supabase
        .from('trades')
        .select('id, proposer_team_id, receiver_team_id, status, votes_for, votes_against')
        .eq('league_id', activeLeague.id)
        .eq('status', 'completed'),
    ]);

    const sabotages = sabRes.data || [];
    const matchups = matchRes.data || [];
    const trades = tradeRes.data || [];

    const teamMap = new Map(teams.map(t => [t.id, t.name]));
    const tn = (id: string) => teamMap.get(id) || 'Unknown';

    const computed: AwardData[] = [];

    // 1. MVP — most total points
    if (standings.length > 0) {
      const sorted = [...standings].sort((a, b) => b.total_points - a.total_points);
      const mvp = sorted[0];
      computed.push({
        id: 'mvp',
        icon: <Crown className="w-5 h-5" />,
        label: 'Season MVP',
        teamName: mvp.team_name,
        value: `${mvp.total_points} pts`,
        color: 'text-primary',
        description: 'Most total trash points scored',
      });
    }

    // 2. Best Record
    if (standings.length > 0) {
      const best = standings[0]; // already sorted by wins
      computed.push({
        id: 'best-record',
        icon: <Trophy className="w-5 h-5" />,
        label: 'Best Record',
        teamName: best.team_name,
        value: `${best.wins}-${best.losses}${best.ties > 0 ? `-${best.ties}` : ''}`,
        color: 'text-primary',
        description: 'Best win-loss record in the league',
      });
    }

    // 3. Worst Team — fewest wins (most losses)
    if (standings.length > 0) {
      const worst = standings[standings.length - 1];
      computed.push({
        id: 'worst-team',
        icon: <Skull className="w-5 h-5" />,
        label: 'Basement Dweller',
        teamName: worst.team_name,
        value: `${worst.wins}-${worst.losses}`,
        color: 'text-destructive',
        description: 'Worst record in the league',
      });
    }

    // 4. Most Sabotages Used
    const sabCountByTeam: Record<string, number> = {};
    const sabSpentByTeam: Record<string, number> = {};
    const sabVictimCount: Record<string, number> = {};
    for (const s of sabotages) {
      sabCountByTeam[s.applied_by_team_id] = (sabCountByTeam[s.applied_by_team_id] || 0) + 1;
      sabSpentByTeam[s.applied_by_team_id] = (sabSpentByTeam[s.applied_by_team_id] || 0) + s.cost;
      sabVictimCount[s.target_team_id] = (sabVictimCount[s.target_team_id] || 0) + 1;
    }

    const topSaboteur = Object.entries(sabCountByTeam).sort((a, b) => b[1] - a[1])[0];
    if (topSaboteur) {
      computed.push({
        id: 'most-sabotages',
        icon: <Bomb className="w-5 h-5" />,
        label: 'Chaos Agent',
        teamName: tn(topSaboteur[0]),
        value: `${topSaboteur[1]} sabotages`,
        color: 'text-primary',
        description: 'Most sabotages deployed this season',
      });
    }

    // 5. Sabotage Victim — most times sabotaged
    const topVictim = Object.entries(sabVictimCount).sort((a, b) => b[1] - a[1])[0];
    if (topVictim) {
      computed.push({
        id: 'sabotage-victim',
        icon: <Target className="w-5 h-5" />,
        label: 'Sabotage Magnet',
        teamName: tn(topVictim[0]),
        value: `${topVictim[1]}× targeted`,
        color: 'text-destructive',
        description: 'Most sabotaged team this season',
      });
    }

    // 6. Most Points in a Week
    const weeklyScores: { team_id: string; score: number; week: number }[] = [];
    for (const m of matchups) {
      weeklyScores.push({ team_id: m.home_team_id, score: Number(m.home_score), week: m.week });
      weeklyScores.push({ team_id: m.away_team_id, score: Number(m.away_score), week: m.week });
    }
    const topWeek = weeklyScores.sort((a, b) => b.score - a.score)[0];
    if (topWeek) {
      computed.push({
        id: 'best-week',
        icon: <Flame className="w-5 h-5" />,
        label: 'Best Single Week',
        teamName: tn(topWeek.team_id),
        value: `${topWeek.score} pts (Wk ${topWeek.week})`,
        color: 'text-primary',
        description: 'Highest single-week score',
      });
    }

    // 7. Biggest Blowout
    const blowouts = matchups.map(m => ({
      winner: Number(m.home_score) > Number(m.away_score) ? m.home_team_id : m.away_team_id,
      loser: Number(m.home_score) > Number(m.away_score) ? m.away_team_id : m.home_team_id,
      margin: Math.abs(Number(m.home_score) - Number(m.away_score)),
      week: m.week,
    })).sort((a, b) => b.margin - a.margin);
    if (blowouts.length > 0) {
      const top = blowouts[0];
      computed.push({
        id: 'biggest-blowout',
        icon: <Zap className="w-5 h-5" />,
        label: 'Biggest Blowout',
        teamName: tn(top.winner),
        value: `+${top.margin.toFixed(1)} pts (Wk ${top.week})`,
        color: 'text-primary',
        description: `Defeated ${tn(top.loser)} by the largest margin`,
      });
    }

    // 8. Biggest Upset — lowest-ranked team beats highest-ranked
    // Use standings rank to determine upsets
    const rankMap = new Map(standings.map((s, i) => [s.team_id, i + 1]));
    const upsets = matchups.map(m => {
      const hs = Number(m.home_score);
      const as_ = Number(m.away_score);
      const winner = hs > as_ ? m.home_team_id : m.away_team_id;
      const loser = hs > as_ ? m.away_team_id : m.home_team_id;
      const winnerRank = rankMap.get(winner) || 99;
      const loserRank = rankMap.get(loser) || 99;
      return { winner, loser, rankDiff: winnerRank - loserRank, week: m.week };
    }).filter(u => u.rankDiff > 0).sort((a, b) => b.rankDiff - a.rankDiff);

    if (upsets.length > 0) {
      const top = upsets[0];
      computed.push({
        id: 'biggest-upset',
        icon: <AlertTriangle className="w-5 h-5" />,
        label: 'Biggest Upset',
        teamName: tn(top.winner),
        value: `Beat #${rankMap.get(top.loser)} (Wk ${top.week})`,
        color: 'text-primary',
        description: `#${rankMap.get(top.winner)} defeated #${rankMap.get(top.loser)}`,
      });
    }

    // 9. Longest Win Streak
    const teamResults: Record<string, ('W' | 'L' | 'T')[]> = {};
    const sortedMatchups = [...matchups].sort((a, b) => a.week - b.week);
    for (const m of sortedMatchups) {
      const hs = Number(m.home_score);
      const as_ = Number(m.away_score);
      if (!teamResults[m.home_team_id]) teamResults[m.home_team_id] = [];
      if (!teamResults[m.away_team_id]) teamResults[m.away_team_id] = [];
      if (hs > as_) {
        teamResults[m.home_team_id].push('W');
        teamResults[m.away_team_id].push('L');
      } else if (as_ > hs) {
        teamResults[m.away_team_id].push('W');
        teamResults[m.home_team_id].push('L');
      } else {
        teamResults[m.home_team_id].push('T');
        teamResults[m.away_team_id].push('T');
      }
    }

    let longestStreak = { teamId: '', count: 0 };
    for (const [tid, results] of Object.entries(teamResults)) {
      let maxStreak = 0;
      let cur = 0;
      for (const r of results) {
        if (r === 'W') { cur++; maxStreak = Math.max(maxStreak, cur); }
        else cur = 0;
      }
      if (maxStreak > longestStreak.count) {
        longestStreak = { teamId: tid, count: maxStreak };
      }
    }
    if (longestStreak.count > 0) {
      computed.push({
        id: 'win-streak',
        icon: <TrendingUp className="w-5 h-5" />,
        label: 'Longest Win Streak',
        teamName: tn(longestStreak.teamId),
        value: `${longestStreak.count}W`,
        color: 'text-primary',
        description: 'Longest consecutive win streak',
      });
    }

    // 10. Trash King — most points scored AGAINST (least "good")
    const pointsAgainst: Record<string, number> = {};
    for (const m of matchups) {
      pointsAgainst[m.home_team_id] = (pointsAgainst[m.home_team_id] || 0) + Number(m.away_score);
      pointsAgainst[m.away_team_id] = (pointsAgainst[m.away_team_id] || 0) + Number(m.home_score);
    }
    const trashKing = Object.entries(pointsAgainst).sort((a, b) => b[1] - a[1])[0];
    if (trashKing) {
      computed.push({
        id: 'trash-king',
        icon: <Star className="w-5 h-5" />,
        label: 'Trash King',
        teamName: tn(trashKing[0]),
        value: `${trashKing[1].toFixed(1)} PA`,
        color: 'text-destructive',
        description: 'Most points scored against them',
      });
    }

    // 11. Best Trade — trade with most community votes_for
    if (trades.length > 0) {
      const bestTrade = [...trades].sort((a, b) => b.votes_for - a.votes_for)[0];
      if (bestTrade.votes_for > 0) {
        computed.push({
          id: 'best-trade',
          icon: <ArrowUpDown className="w-5 h-5" />,
          label: 'Best Trade',
          teamName: tn(bestTrade.proposer_team_id),
          value: `${bestTrade.votes_for} approvals`,
          color: 'text-primary',
          description: 'Most community-approved trade',
        });
      }
    }

    // 12. Comeback Kid — team with best improvement from first half to second half
    const midWeek = Math.ceil(Math.max(...matchups.map(m => m.week)) / 2);
    const halfRecords: Record<string, { first: number; second: number }> = {};
    for (const m of sortedMatchups) {
      const hs = Number(m.home_score);
      const as_ = Number(m.away_score);
      for (const tid of [m.home_team_id, m.away_team_id]) {
        if (!halfRecords[tid]) halfRecords[tid] = { first: 0, second: 0 };
        const isHome = tid === m.home_team_id;
        const won = isHome ? hs > as_ : as_ > hs;
        if (won) {
          if (m.week <= midWeek) halfRecords[tid].first++;
          else halfRecords[tid].second++;
        }
      }
    }
    const comebacks = Object.entries(halfRecords)
      .map(([tid, h]) => ({ tid, improvement: h.second - h.first }))
      .filter(c => c.improvement > 0)
      .sort((a, b) => b.improvement - a.improvement);
    if (comebacks.length > 0) {
      computed.push({
        id: 'comeback-kid',
        icon: <Award className="w-5 h-5" />,
        label: 'Comeback Kid',
        teamName: tn(comebacks[0].tid),
        value: `+${comebacks[0].improvement}W in 2nd half`,
        color: 'text-primary',
        description: 'Best improvement from first to second half',
      });
    }

    setAwards(computed);
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-28" />
        ))}
      </div>
    );
  }

  if (awards.length === 0) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold font-heading text-foreground flex items-center gap-2">
          <Trophy className="w-5 h-5 text-primary" />
          {isCompleted ? 'Season Awards' : 'Award Projections'}
        </h2>
        {!isCompleted && (
          <Badge variant="outline" className="text-[10px] border-primary/30 text-primary">
            PROJECTED
          </Badge>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {awards.map((award) => (
          <Card
            key={award.id}
            className={cn(
              "bg-card border-border overflow-hidden transition-all hover:border-primary/30",
              isCompleted && "border-primary/20"
            )}
          >
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-2">
                <div className={cn("shrink-0", award.color)}>
                  {award.icon}
                </div>
                <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide leading-tight">
                  {award.label}
                </span>
              </div>
              <p className="text-sm font-bold text-foreground truncate">{award.teamName}</p>
              <p className={cn("text-xs font-semibold mt-0.5", award.color)}>{award.value}</p>
              <p className="text-[10px] text-muted-foreground mt-1 leading-snug line-clamp-2">{award.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default SeasonAwards;
